import * as _ from "lodash";
import { Dispatcher, Position, Service } from "puzzle";
import { NOTIFICATION } from "../../../Viewport/ToastNotifications/ToastNotifications";
import { FILE } from "../../../SubmissionsList/ActionToolbar/UploadDialog/ReportDialog/Types";
import { PromoteDemoteAbstract } from "../../../Drawer/Tabs/Files/PromoteDemoteAbstract";
import { WORKFLOW_CONFIGURATION } from "../../../SubmissionsList/ActionToolbar/ActionToolbar";
import { copyToClipboard, getOnlineEditorIdentifier, TSC_EDITORS } from "../../../util/Util";
import {
  ONLINE_EDITOR,
  PRECONDITION_CHECK_RESPONSE_PARAMS,
  TARGET,
  TARGET_VIEW_RESPONSE
} from "../../../Drawer/Tabs/Types";
import { SUBMISSION_DATA } from "../../../Drawer/Types";

type PING_DATA = {
  ipcEnabled: boolean
  processingFinished: boolean
  promoteDemoteEnabled: boolean
  uploadReportsDto: null | FILE[]
};

type URLS_RESPONSE = {
  captive: boolean
  editorUrl: string
  entityIds: number[]
  entityType: string
  errorMessage: null | any
}

//TODO import this to FILES tab and remove copies of methods
class ActionExecutor extends Service {
  private static _instance: any;
  editorSessionIds: any[] = [];
  onlineEditors: { windowRef: Window, editor: ONLINE_EDITOR }[] = [];
  promoteDemote: any = {}
  pingingInstance: string = '';
  pingingInterval: any = null;

  public static get Instance() {
    return this._instance || (this._instance = new this());
  }

  constructor() {
    super();
    this.registerServiceToDispatcher();
    this.promoteDemote = new PromoteDemoteAbstract({});
  }

  /**
   * Clears warning flags
   */
  clearWarringFlags = () => {
    this.promoteDemote.clearWarringFlags();
  };

  /**
   * Copy file path to clipboard button action
   *
   * @param text
   */
  copyToClipboardBtnAction = (text: string) => {
    copyToClipboard(text);
  };

  /**
   * Returns comment params
   *
   * @param actionName
   * @param requestData
   */
  createCommentsRequestParams = (actionName: string, requestData: any = {}) => {
    let comment = this.promoteDemote.createCommentsRequestParams(actionName, requestData);
    Object.assign(comment.activeFilters, {
      ...comment.activeFilters,
      folder: "JOB_PHASE"
    });
    return comment;
  };

  /**
   * Check if complete/return action can be executed
   * @param action
   * @param data
   * @param ignoreAdditionalValidation
   * @param mandatoryComments
   * @param skipClaimedCondition
   * @param submissionData
   */
  checkIfCompleteCanBeExecuted = (
    action: string,
    data: WORKFLOW_CONFIGURATION,
    ignoreAdditionalValidation: boolean,
    mandatoryComments: { mandatoryGradingAndScoringPresent: boolean | undefined } | undefined,
    skipClaimedCondition: boolean = false,
    submissionData: { phaseType: string | null }
  ) => {
    let canBeExecuted = true;
    let dispatchAction;
    let actionParams = {};
    const mandatoryGradingAndScoringPresent = mandatoryComments?.mandatoryGradingAndScoringPresent;

    if (!ignoreAdditionalValidation || ((_.get(data, 'gradingAndScoring') === 'requiredMandatory') && !mandatoryGradingAndScoringPresent)) {
      if (!skipClaimedCondition && data.hasClaimedFiles) {
        canBeExecuted = false;
        dispatchAction = 'samePhaseWarning';
        actionParams = {
          actionNameLocale: this.translate(`promoteDemoteLocale.${action}`).toLowerCase(),
        };
      } else if (_.get(submissionData, 'phaseType') === 'linguist' && !_.get(data, 'allFilesUploaded') && !data.hasCompletedFiles) {
        canBeExecuted = false;
        dispatchAction = 'linguistWithoutUploadWarning';
      } else if (!skipClaimedCondition && data.hasCompletedFiles) {
        canBeExecuted = false;
        dispatchAction = 'hasCompletedFilesWarning';
      }
    }

    let result = {
      canBeExecuted, dispatchAction, actionParams
    }
    return result;
  }

  /**
   * Downloads instructions via blob download
   * @param result
   */
  downloadFile = (result: any) => {
    if (_.isString(result)) {
      const link = document.createElement('a');
      const blobObject = new Blob([result], { type: 'text/html' });
      let title: any = result.match(/<title>.*<\/title>/);
      if (title) {
        title = title[0];
        title = title.replace(/<title>|<\/title>/g, '');
      }

      const href = window.URL.createObjectURL(blobObject);
      link.href = href;
      link.setAttribute('download', title);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(href);
    }
  };

  /**
   * Flow for open action from main grid
   */
  executeOpenTask = (selectedTask: any, editor?: string) => {
    if (!editor) {
      editor = _.get(selectedTask, 'availableEditors[0]');
    }

    this.dispatch('getTaskOnlineEditorAddress', Position.end, {
      onlineEditorParams: {
        editorName: editor,
        preview: false
      },
      selectedTask
    });
  };

  /**
   * Returns true if passed dispatcher name exists, false otherwise
   */
  getAvailableDispatcher = (dispatcherName: string) => {
    return !!_.get(Dispatcher, `appDispatchers.${dispatcherName}`);
  };

  /**
   * PromoteDemoteComment prop getter.
   */
  getComment = () => {
    return this.promoteDemote.getComment();
  };

  /**
   * Check of module is registered/mounted on dispatcher
   */
  getAppModule = (moduleName: string) => {
    return !!_.get(Dispatcher, `appModules.${moduleName}[0]`);
  }

  /**
   * Get localization used for dispatched dialog name and button
   * @param action
   */
  getLocalization = (action: 'promote' | 'demote') => {
    const key = 'promoteDemoteLocale';
    let locale = {
      btnLocale: this.translate(`${key}.${action}`),
      titleLocale: this.translate(`${key}.${action}Title`)
    };

    return locale;
  };

  /**
   * Returns warning flags
   *
   * @param actionName
   */
  getWarningFlags = (actionName: string, selectAll: boolean) => {
    return this.promoteDemote.getWarningFlags(actionName, selectAll);
  };

  /**
   * Opens new window url received form URLS request.
   * @param onlineEditorObject
   * @param requestParamsEditor
   * @param editorName
   */
  prepareEditorOpen = (onlineEditorObject: URLS_RESPONSE[], {
    editor: requestParamsEditor,
    editorName
  }: { editor: ONLINE_EDITOR, editorName: ONLINE_EDITOR }): void => {
    // on opening of processed file server returns empty array
    let onlineEditor = onlineEditorObject[0];
    const {
      captive,
      editorUrl
    } = onlineEditor;
    const time = new Date().getTime();
    // default params configuration is set to TSC
    let editor: ONLINE_EDITOR = requestParamsEditor || editorName;
    let editorIdentifierParams: Parameters<typeof getOnlineEditorIdentifier> = [editorUrl, 'payloadId'];
    let windowParams: Parameters<typeof window.open> = [editorUrl];

    if (!onlineEditor.editorUrl.includes('payloadId')) { // TSC link contains payloadId instead of editorSessionId
      const captiveModeParams = '"location=no,status=no,toolbar=no,titlebar=no,menubar=no,directories=no,personalbar=no,dialog=yes,chrome=yes,resizable",';
      const urlWithTheme = editorUrl + '&theme=classicPrimavera';
      editor = 'TSO/TSR';
      editorIdentifierParams = [editorUrl];
      windowParams = [urlWithTheme];

      if (onlineEditor && captive) {
        windowParams = [urlWithTheme, '', captiveModeParams];
      }
    }

    this.onlineEditors[time] = {
      windowRef: window.open(...windowParams) as Window,
      editor
    };
    this.editorSessionIds[time] = getOnlineEditorIdentifier(...editorIdentifierParams);
    //TODO if multi editors are required every selected task needs to be saved here.


    if (!this.pingingInterval) {
      setTimeout(this.m_pingOnlineEditorWindow, 1000, this);
    }
  };

  /**
   * Pings opened browser window's for information if window was closed
   */
  m_pingOnlineEditorWindow = (): void => {
    if (!this.pingingInterval) {
      this.pingingInterval = setInterval(this.m_pingOnlineEditorWindow, 1500, this);
    }
    for (const time in this.onlineEditors) {
      let externalEditor = this.onlineEditors[time].windowRef;
      if (externalEditor && externalEditor.closed) {
        //required for detecting closing tso/tsr/tsc so correct editorSessionId/payloadId can be sent
        const editorSessionId = this.editorSessionIds[time];
        delete this.editorSessionIds[time];

        const editorType: string = this.onlineEditors[time].editor;
        delete this.onlineEditors[time];

        if (!_.keys(this.onlineEditors).length) {
          clearInterval(this.pingingInterval);
          this.pingingInterval = null;
        }

        this.processOnlineEditorClosing(editorSessionId, editorType);
      }
    }
  };

  /**
   * Opens task in tso/tsr in preview mode,Opens task in tso/tsr in preview mode, also used to open files in GL_PLAY EDITOR(REVIEW, TRANSLATION, TRANSSCRIPT)
   *
   * @param onlineEditorObject
   * @param addTheme
   */
  openInPreviewMode = (onlineEditorObject: URLS_RESPONSE[], addTheme = true): void => {
    let url = _.get(onlineEditorObject, '[0].editorUrl');
    if (addTheme) {
      url += '&theme=classicPrimavera';
    }
    window.open(url);
  };

  /**
   * Flow for preview action
   *
   * @param selectedTask
   * @param editor
   * @param url
   * @param method
   */
  previewTask = (selectedTask: any, editor?: object, url?: string, method?: string): void => {
    if (!editor) {
      editor = {
        editorName: _.get(selectedTask, 'availableEditors[0]')
      }
    }
    if (!url) {
      url = `/jobPhases/${selectedTask.taskId}/editor/urls`;
    }
    if (!method) {
      method = 'get';
    }

    this.dispatch('previewTask', Position.end, {
      onlineEditorParams: {
        preview: true,
        ...editor
      },
      previewTaskUrl: url,
      selectedTask,
      method
    });
  };

  /**
   * Starts batch pinging through dispatcher
   * @param editorSessionId
   * @param editorType
   */
  processOnlineEditorClosing = (editorSessionId: number, editorType: string): void => {
    let actionName = 'processSupplierEditorClose';
    let dispatcher = {};
    if (this.pingingInstance === 'ReopenDialog') {
      dispatcher = { dispatcherId: 'reopenTsoPing' };
      actionName = 'reopenProcessEditorClose';
    } else if (_.includes(TSC_EDITORS, editorType)) {
      actionName = 'processTscClose';
    }

    this.dispatch(actionName, {
      pos: Position.end,
      persistent: ['drawerDetached'],
      ...dispatcher
    }, {
      drawerDetached: false,
      pingParam: {
        editorSessionId
      }
    });
  };

  /**
   * Setter for pinging instance value
   * @param name
   */
  setPingingInstance = (name: string) => {
    this.pingingInstance = name;
  }
  /**
   * Reset pinging instance
   */
  resetPingingInstance = () => {
    this.pingingInstance = '';
  }

  /**
   * Returns tso upload success notification
   */
  getTSOUploadNotification = (pingData: PING_DATA): NOTIFICATION => {
    let {
      uploadReportsDto,
      ipcEnabled
    } = pingData;
    let numberOfFiles = 0;

    if (uploadReportsDto?.length) {
      numberOfFiles = uploadReportsDto.reduce((count: number, item: FILE) => {
        if (item.fileUploadStatus === 'UPLOADED') {
          count++;
        }
        return count;
      }, 0);
    }

    return {
      text: this.translate(ipcEnabled ? 'ipcNotificationText' : 'notificationText', { file: numberOfFiles }),
      type: 'success',
      icon: true,
      closable: true
    }
  };

  /**
   * Sets flag configuration
   *
   * @param actionName
   * @param configuration
   */
  setFlagConfiguration = (actionName: string, configuration: any) => {
    this.promoteDemote.setFlagConfiguration(actionName, configuration);
  };

  /**
   * Set mandatory comments flag,
   * also used for Grading and Scoring for checking is mandatory grade and score set on all targets.
   * @param action
   * @param responseData
   */
  setMandatoryCommentsFlag = (action: 'promote' | 'demote', responseData: PRECONDITION_CHECK_RESPONSE_PARAMS) => {
    this.promoteDemote.setMandatoryCommentsFlag(action, responseData);
  };

  /**
   * Sets warning flags
   * @param flagForChange
   * @param commentValue
   */
  setWarningFlags = (flagForChange: string | string[], commentValue: string = '') => {
    this.promoteDemote.setWarningFlags(flagForChange, commentValue);
  };

  /**
   * Creates flag setup if GS has comment, to avoid showing promoteCommentMandatoryEnabled or demoteCommentMandatoryEnabled comment dialog
   * @param actionName
   */
  prepareGsFlags = (actionName: string) => this.promoteDemote.prepareGsFlags(actionName);

  /**
   * Get mandatory comment and mandatory grading and scoring enabled flag
   * @param action
   * @param selectAll
   */
  getMandatoryCommentEnabled = (action: 'promote' | 'demote', selectAll: boolean) => {
    return this.promoteDemote.getMandatoryCommentEnabled(action, selectAll);
  };

  /**
   * Prepares targets for grading and scoring dialog if actions was triggered from TaskList
   * @param gsTargets
   * @param submissionData
   */
  transformRequestDataForGradingAndScoring = (gsTargets: TARGET_VIEW_RESPONSE, submissionData: SUBMISSION_DATA) => {
    let data = gsTargets.items.filter((item: TARGET) => !['CANCELLED', 'CLAIMED', 'COMPLETED'].includes(item.status.name));
    let total = data.length;
    this.promoteDemote.gradeAndScore(
      { all: true },
      { data, total },
      {
        submissionData,
        isSupplierView: true
      }
    );
  };
}

const ActionExecutorInstance: ActionExecutor = ActionExecutor.Instance;
export default ActionExecutorInstance;
